import { Flex, Loading, Toast } from '@byecode/ui'
import { useAtomAction } from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'
import { useAsyncRetry } from 'react-use'
import styled from 'styled-components'

import { fetchUserAtom } from '@/atoms/auth/action'
import { userDataAtom } from '@/atoms/auth/state'
import { workSpaceAtom } from '@/atoms/workSpace/state'
import { QuotaDayWarningModal } from '@/components/QuotaDayWarningModal'
import { SpaceAlert } from '@/components/SpaceAlert'
import { Profile } from '@/containers/Profile'
import { Promotion } from '@/containers/Promotion'
import { SpaceContext } from '@/contexts/SpaceContext'

interface ByecodeProps {}

const SCxContainer = styled(Flex)`
    width: 100%;
    height: 100%;
`

const SCxMain = styled.div`
    width: 100%;
    flex: 1;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
`

const EXPIRED_AT_THRESHOLD = 1000 * 60 * 60 * 24 * 30 // 30天
// enum STATUS {
//     ENTER = 0,
//     FETCHED = 1,
//     UPDATED = 2
// }
export const Byecode: React.FC<ByecodeProps> = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [open, setOpen] = useState(false)
    const [isUserSpaceAlertClose, setIsUserSpaceAlertClose] = useState(sessionStorage.getItem('space_alert_close') === 'true')
    const [promotionOpen, setPromotionOpen] = useState(false)
    const currentSpace = useAtomValue(workSpaceAtom)
    const { expiredAt } = currentSpace
    const user = useAtomValue(userDataAtom)
    const { run: fetchUser } = useAtomAction(fetchUserAtom)
    const { loading, value } = useAsyncRetry(() => {
        if (!user.userId) {
            return fetchUser()
        }
        return Promise.resolve(user)
    }, [])

    const isReadyExpire = useMemo(() => {
        if (isUserSpaceAlertClose) {
            return false
        }
        if (!expiredAt || expiredAt === -1) {
            return false
        }
        if (expiredAt <= Date.now()) {
            return false
        }
        return expiredAt <= Date.now() + EXPIRED_AT_THRESHOLD
    }, [expiredAt, isUserSpaceAlertClose])

    const handleCloseSpaceAlert = useCallback(() => {
        sessionStorage.setItem('space_alert_close', 'true')
        setIsUserSpaceAlertClose(true)
    }, [setIsUserSpaceAlertClose])
    // 获取邀请用户的活动 暂无活动
    // const inviteActivity = useMemo(() => find(act => act.type === ActivityType.INVITATION_USER, activityList || []), [activityList])

    // useEffect(() => {
    //     fetchActivityList()
    // }, [fetchActivityList])

    const userId = value?.userId
    const username = value?.username

    const handleInviteUser = useCallback(() => {
        setPromotionOpen(true)
    }, [])

    const handleClose = useCallback(() => {
        setOpen(false)
    }, [])

    // 如果是新人则打开邀请用户优惠弹框
    // useUpdateEffect(() => {
    //     if (!invitationActivity && user.userId) {
    //         setPromotionOpen(true)
    //     }
    // }, [user.isNewUser])

    useEffect(() => {
        if (loading) {
            return
        }
        if (!userId) {
            return
        }
        if (!username) {
            navigate(
                { pathname: '/account/perfect', search: searchParams.toString() },
                {
                    replace: true
                }
            )
        }
    }, [loading, navigate, searchParams, userId, username, value])

    const spaceContextValue = useMemo(
        () => ({
            open,
            setOpen,
            promotionOpen,
            setPromotionOpen
        }),
        [open, promotionOpen]
    )

    return (
        <SCxContainer direction="column">
            {useMemo(() => {
                if (loading) {
                    return <Loading />
                }
                if (!value) {
                    return null
                }
                return (
                    <>
                        {isReadyExpire && <SpaceAlert expiredAt={expiredAt} onClose={handleCloseSpaceAlert} />}
                        <SCxMain>
                            <SpaceContext.Provider value={spaceContextValue}>
                                <Outlet context={value} />
                                <Profile open={open} onClose={handleClose} onInviteUser={handleInviteUser} />
                                {promotionOpen && <Promotion open={promotionOpen} onChange={setPromotionOpen} />}
                                <QuotaDayWarningModal />
                            </SpaceContext.Provider>
                        </SCxMain>
                    </>
                )
            }, [
                expiredAt,
                handleClose,
                handleCloseSpaceAlert,
                handleInviteUser,
                isReadyExpire,
                loading,
                open,
                promotionOpen,
                spaceContextValue,
                value
            ])}
        </SCxContainer>
    )
}
